import React, { useState, useEffect } from "react";

import { useFormikContext } from "formik";
import { Plus, Close } from "icons";

import { Typography, Tab, Button } from "clearsense-ui/components";
import PermissibleWrapper from "components/Common/PermissibleWrapper";

import Form from "./Form";

import {
  CATEGORIES,
  NON_EDITABLE_FIELDS,
  TABS_WITH_SINGLE_SUBTAB,
} from "../constants";
import { generateFields } from "../utils";

const EditForm = ({ data, isViewMode, userPermissions, searchParams }) => {
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].value);
  const [subTab, setSubTab] = useState(0);
  const [tabsCount, setTabsCount] = useState(0);
  const formik = useFormikContext();

  let tabNumber = 1;

  const getSubTabLabel = value =>
    CATEGORIES.find(category => category.value === value).label;

  useEffect(() => {
    setTabsCount(formik.values?.[activeTab]?.length);
  }, [activeTab]);

  const handleSubTabRemove = (e, index) => {
    e.stopPropagation();
    const valuesCopy = [...formik.values[activeTab]];
    index >= formik?.initialValues?.[activeTab].length
      ? valuesCopy.splice(index, 1)
      : valuesCopy.splice(index, 1, {
          ...valuesCopy[index],
          hidden: true,
        });
    formik.setFieldValue(activeTab, [...valuesCopy]);
    setTabsCount(valuesCopy.length);
    setSubTab(
      valuesCopy.length -
        1 -
        [...valuesCopy].reverse().findIndex(item => !item?.hidden)
    );
  };

  const handleDeleteAll = () => {
    const valuesCopy = [...formik.initialValues[activeTab]].map(item => ({
      ...item,
      hidden: true,
    }));
    formik.setFieldValue(activeTab, valuesCopy);
    setTabsCount(valuesCopy.length);
    setSubTab(
      valuesCopy.length -
        1 -
        [...valuesCopy].reverse().findIndex(item => !item?.hidden)
    );
  };

  return (
    <div className="space-y-8">
      <div className=" w-full flex flex-col space-y-4 justify-start rounded-md">
        {NON_EDITABLE_FIELDS.map(
          (field, index) =>
            data[field.value] && (
              <div
                key={index}
                className="flex items-center space-x-4 p-5 border bg-white"
              >
                <Typography
                  style="h4"
                  type="semibold"
                  className="text-shades-darkest text-center"
                >
                  {field.label}:
                </Typography>
                <div className="flex space-x-2 border p-3">
                  {Object.entries(field.fields).map(([value, label], index) => (
                    <div key={index} className="flex flex-wrap space-x-2 p-2">
                      <div className="flex-shrink-0">
                        <Typography style="medium">{label}:</Typography>
                      </div>
                      <div className="text-primary-shade">
                        <Typography style="medium">
                          {data[field.value][0][value] ?? "Nil"}
                        </Typography>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
        )}
      </div>
      <div className="flex flex-col space-y-2">
        <Tab
          className="overflow-x-auto overflow-y-hidden cs-ui-scrollbar"
          style="primary"
        >
          {CATEGORIES.map(category => (
            <Tab.Item
              active={category.value === activeTab}
              key={category.value}
              className="flex-none flex-grow"
              onClick={() => {
                setActiveTab(category.value);
                setSubTab(
                  formik?.values?.[category.value]?.findIndex(
                    item => !item?.hidden
                  )
                );
              }}
            >
              {category.label}
            </Tab.Item>
          ))}
        </Tab>
        <div className="flex items-start justify-between">
          <div className="overflow-x-auto overflow-y-hidden mr-4 cs-ui-scrollbar">
            {!TABS_WITH_SINGLE_SUBTAB.includes(activeTab) && (
              <Tab style="secondary">
                {Array(tabsCount)
                  .fill()
                  .map(
                    (_, index) =>
                      !formik?.values?.[activeTab][index]?.hidden && (
                        <Tab.Item
                          key={index}
                          active={index === subTab}
                          className="flex-none capitalize flex-grow px-8"
                          onClick={() => setSubTab(index)}
                        >
                          {`${getSubTabLabel(activeTab)} ${tabNumber++}`}
                          {!isViewMode && (
                            <PermissibleWrapper
                              currentPermissions={userPermissions}
                              acceptablePermissions="destroy"
                            >
                              <Button
                                style="danger"
                                size="small"
                                icon={Close}
                                className="absolute right-0 rounded-full"
                                onClick={e => handleSubTabRemove(e, index)}
                              />
                            </PermissibleWrapper>
                          )}
                        </Tab.Item>
                      )
                  )}
                {!isViewMode && (
                  <PermissibleWrapper
                    currentPermissions={userPermissions}
                    acceptablePermissions="create"
                  >
                    <Tab.Item
                      className="flex-none capitalize flex-grow px-4"
                      onClick={() => {
                        const newValue = [
                          ...formik.values[activeTab],
                          {
                            subject_id: data?.primary?.[0]?.subject_id,
                            rec_create_dt:
                              searchParams?.credentialing_dashboard
                                ?.rec_create_dt,
                            ...generateFields(0)[activeTab].reduce(
                              (acc, curr) => {
                                const fieldAccessor = curr.name
                                  .split(".")
                                  .pop();
                                if (fieldAccessor.includes("is_")) {
                                  return {
                                    ...acc,
                                    [curr.name.split(".").pop()]: "N",
                                  };
                                }

                                return {
                                  ...acc,
                                  [curr.name.split(".").pop()]: "",
                                };
                              },
                              {}
                            ),
                          },
                        ];
                        formik?.setFieldValue(activeTab, newValue);
                        const copy = [...newValue];
                        setTabsCount(formik?.values?.[activeTab]?.length + 1);
                        setSubTab(
                          newValue?.length -
                            1 -
                            copy?.reverse().findIndex(item => !item?.hidden)
                        );
                      }}
                    >
                      <Plus />
                      Add
                    </Tab.Item>
                  </PermissibleWrapper>
                )}
              </Tab>
            )}
          </div>
          {!TABS_WITH_SINGLE_SUBTAB.includes(activeTab) &&
            !isViewMode &&
            !!formik.values[activeTab].filter(item => !item.hidden).length && (
              <PermissibleWrapper
                currentPermissions={userPermissions}
                acceptablePermissions="destroy"
              >
                <Button
                  label="Delete All"
                  style="danger"
                  icon={Close}
                  className="flex-shrink-0"
                  onClick={handleDeleteAll}
                />
              </PermissibleWrapper>
            )}
        </div>
        <Form
          activeTab={activeTab}
          subTab={subTab}
          isViewMode={isViewMode}
          userPermissions={userPermissions}
        />
      </div>
    </div>
  );
};

export default EditForm;
